import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Phone, ChevronDown } from "lucide-react";
import { HERO_URL, LOGO_URL, CALL_LINK } from "../data";
import { WhatsAppIcon } from "./WhatsAppIcon";

const REVEAL_EASE = [0.22, 1, 0.36, 1];

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "20%"]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1.06, 1.2]);
  const contentY = useTransform(scrollYProgress, [0, 1], ["0%", "38%"]);
  const contentOpacity = useTransform(scrollYProgress, [0, 0.75], [1, 0]);

  return (
    <section
      ref={ref}
      id="inicio"
      data-testid="hero-section"
      className="relative min-h-screen flex items-center justify-center overflow-hidden"
    >
      <motion.div
        style={{ y: imgY, scale: imgScale }}
        className="absolute inset-0 will-change-transform"
      >
        <img
          src={HERO_URL}
          alt="Tacos de barbacoa La Herencia Castañón"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-brand-base via-brand-base/75 to-black/50" />
        <div className="absolute inset-0 bg-gradient-to-r from-brand-base/70 via-transparent to-brand-base/40" />
      </motion.div>

      <motion.div
        style={{ y: contentY, opacity: contentOpacity }}
        className="relative z-10 max-w-4xl mx-auto px-6 text-center pt-28 pb-20 will-change-transform"
      >
        <motion.img
          initial={{ opacity: 0, scale: 0.85 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.9, ease: "easeOut" }}
          src={LOGO_URL}
          alt="La Herencia Castañón"
          data-testid="hero-logo"
          className="w-40 h-40 md:w-52 md:h-52 object-cover mx-auto rounded-full ring-2 ring-brand-gold/50 shadow-2xl shadow-black/60 bg-white"
        />

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.3 }}
          className="mt-8 text-brand-gold uppercase tracking-[0.35em] text-xs md:text-sm"
        >
          Barbacoa tradicional &amp; al chipotle · 100% de res
        </motion.p>

        <motion.h1
          className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold text-brand-cream mt-5 leading-[1.05]"
        >
          <span className="block overflow-hidden pb-1.5">
            <motion.span
              className="block"
              initial={{ y: "115%" }}
              animate={{ y: 0 }}
              transition={{ duration: 0.95, delay: 0.5, ease: REVEAL_EASE }}
            >
              El sabor de una herencia
            </motion.span>
          </span>
          <span className="block overflow-hidden pb-1.5">
            <motion.span
              className="block text-brand-gold italic font-medium"
              initial={{ y: "115%" }}
              animate={{ y: 0 }}
              transition={{ duration: 0.95, delay: 0.66, ease: REVEAL_EASE }}
            >
              que se sirve en familia
            </motion.span>
          </span>
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.6 }}
          className="mt-6 text-brand-sand text-base md:text-lg max-w-2xl mx-auto leading-relaxed"
        >
          Barbacoa de res cocida lento y a la antigua en La Junta, Chihuahua.
          Tacos, lonches y consomé hechos con la receta de casa, calidad y
          tradición en cada bocado.
        </motion.p>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.68 }}
          className="mt-7 font-display italic text-brand-gold text-2xl md:text-3xl"
        >
          «Somos la cura para tu cruda»
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.75 }}
          className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4"
        >
          <a
            href="#menu"
            data-testid="hero-whatsapp-btn"
            className="w-full sm:w-auto inline-flex items-center justify-center gap-2.5 bg-brand-gold text-brand-base font-bold uppercase tracking-widest text-sm px-8 py-4 hover:bg-brand-cream transition-colors duration-300"
          >
            <WhatsAppIcon className="w-5 h-5" />
            Haz tu pedido en línea
          </a>
          <a
            href={CALL_LINK}
            data-testid="hero-call-btn"
            className="w-full sm:w-auto inline-flex items-center justify-center gap-2.5 bg-transparent border border-brand-gold text-brand-gold font-bold uppercase tracking-widest text-sm px-8 py-4 hover:bg-brand-gold hover:text-brand-base transition-colors duration-300"
          >
            <Phone className="w-5 h-5" />
            Llamar ahora
          </a>
        </motion.div>
      </motion.div>

      <a
        href="#especialidades"
        aria-label="Bajar a especialidades"
        className="absolute bottom-8 left-1/2 -translate-x-1/2 text-brand-gold/70 hover:text-brand-gold animate-bounce"
      >
        <ChevronDown className="w-7 h-7" />
      </a>
    </section>
  );
};
